import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPatients, addVital, addLog, getPatientVitals, addEditRequest, updateVital } from '../../utils/storage';
import { getUser } from '../../utils/auth';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function HeightWeightEntryPage() {
  const navigate = useNavigate();
  const user = getUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const [height, setHeight] = useState('');
  const [heightUnit, setHeightUnit] = useState<'cm' | 'feet'>('cm');
  const [weight, setWeight] = useState('');

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [alreadyRecorded, setAlreadyRecorded] = useState(false);
  const [existingVital, setExistingVital] = useState<VitalReading | null>(null);

  // Update request modal
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newHeight, setNewHeight] = useState('');
  const [newHeightUnit, setNewHeightUnit] = useState<'cm' | 'feet'>('cm');
  const [newWeight, setNewWeight] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  if (!user || !['height_weight', 'admin', 'master_admin', 'all_vitals'].includes(user.role)) {
    navigate('/dashboard');
    return null;
  }

  const isAdmin = (): boolean => {
    return user?.role === 'master_admin' || user?.role === 'admin';
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setSelectedPatient(null);
    setAlreadyRecorded(false);
    setExistingVital(null);
    if (query.trim().length > 0) {
      const patients = await getPatients();
      const filtered = patients.filter(
        (p) =>
          p.id.toLowerCase().includes(query.toLowerCase()) ||
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.contactNumber.includes(query)
      );
      setSearchResults(filtered);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchQuery(`${patient.name} (${patient.id})`);
    setShowDropdown(false);
    setSearchResults([]);
    const patientVitals = await getPatientVitals(patient.id);
    const hwVital = patientVitals.find(v => v.type === 'height_weight');
    if (hwVital) {
      setAlreadyRecorded(true);
      setExistingVital(hwVital);
      setMessage(null);
      if (isAdmin()) {
        const [heightPart, weightPart] = hwVital.value.split(' / ');
        const heightValue = parseFloat(heightPart.replace('cm', ''));
        const weightValue = parseFloat(weightPart.replace('kg', ''));
        setHeight(heightValue.toString());
        setWeight(weightValue.toString());
        setHeightUnit('cm');
      }
    } else {
      setAlreadyRecorded(false);
      setExistingVital(null);
      setHeight('');
      setWeight('');
    }
  };

  const convertFeetToCm = (feet: number): number =>
    Math.round(feet * 30.48 * 100) / 100;

  const getHeightInCm = (h: string, unit: 'cm' | 'feet'): number => {
    const value = parseFloat(h);
    return unit === 'feet' ? convertFeetToCm(value) : value;
  };

  // BMI Calculation
  const calculateBMI = (h: string, hUnit: 'cm' | 'feet', w: string): { bmi: number; category: string; color: string } | null => {
    if (!h || !w) return null;
    
    const heightValue = parseFloat(h);
    const weightValue = parseFloat(w);
    
    if (isNaN(heightValue) || isNaN(weightValue) || heightValue <= 0 || weightValue <= 0) return null;
    
    const heightInCm = hUnit === 'feet' ? heightValue * 30.48 : heightValue;
    const heightInM = heightInCm / 100;
    const bmi = weightValue / (heightInM * heightInM);
    
    let category = '';
    let color = '';
    
    if (bmi < 18.5) {
      category = 'Underweight';
      color = 'text-blue-600';
    } else if (bmi >= 18.5 && bmi < 25) {
      category = 'Normal';
      color = 'text-green-600';
    } else if (bmi >= 25 && bmi < 30) {
      category = 'Overweight';
      color = 'text-orange-600';
    } else {
      category = 'Obese';
      color = 'text-red-600';
    }
    
    return { bmi: parseFloat(bmi.toFixed(1)), category, color };
  };

  const bmiData = calculateBMI(height, heightUnit, weight);
  const newBmiData = calculateBMI(newHeight, newHeightUnit, newWeight);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) { setMessage({ type: 'error', text: 'Please select a patient first' }); return; }
    
    // Non-admin cannot submit if already recorded
    if (alreadyRecorded && !isAdmin()) { 
      setMessage({ type: 'error', text: 'Height & Weight already recorded. Use "Request Update" to change it.' }); 
      return; 
    }
    
    if (!height || !weight) { setMessage({ type: 'error', text: 'Please enter both height and weight' }); return; }

    const heightValue = parseFloat(height);
    const weightValue = parseFloat(weight);
    if (isNaN(heightValue) || heightValue <= 0) { setMessage({ type: 'error', text: 'Please enter a valid height' }); return; }
    if (isNaN(weightValue) || weightValue <= 0) { setMessage({ type: 'error', text: 'Please enter a valid weight' }); return; }

    setLoading(true);
    const heightInCm = getHeightInCm(height, heightUnit);
    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();

    const vitalData: VitalReading = {
      id: existingVital?.id || `V${Date.now()}_HEIGHT`,
      patientId: selectedPatient.id,
      type: 'height_weight',
      value: `${heightInCm}cm / ${weightValue}kg`,
      unit: 'cm/kg',
      recordedAt: timestamp,
      recordedBy: user.username,
    };

    // If admin and already recorded, update; otherwise add new
    if (alreadyRecorded && isAdmin() && existingVital) {
      updateVital(vitalData);
      addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'Height & Weight Update',
        details: `Updated Height ${heightInCm} cm and Weight ${weightValue} kg for patient ${selectedPatient.name} (${selectedPatient.id})`,
        ipAddress, deviceName: device, browser, timestamp,
      });
      setMessage({ type: 'success', text: 'Height and weight updated successfully!' });
    } else {
      addVital(vitalData);
      addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'Height & Weight Entry',
        details: `Recorded Height ${heightInCm} cm and Weight ${weightValue} kg for patient ${selectedPatient.name} (${selectedPatient.id})`,
        ipAddress, deviceName: device, browser, timestamp,
      });
      setMessage({ type: 'success', text: 'Height and weight recorded successfully!' });
    }

    setHeight(''); setWeight(''); setHeightUnit('cm');
    setLoading(false);

    setTimeout(() => {
      setMessage(null); setSelectedPatient(null); setSearchQuery('');
      setAlreadyRecorded(false); setExistingVital(null);
    }, 3000);
  };

  const handleUpdateRequest = async () => {
    if (!selectedPatient || !existingVital) return;
    if (!newHeight || !newWeight) { setMessage({ type: 'error', text: 'Please enter both new height and weight.' }); return; }
    if (!updateReason.trim()) { setMessage({ type: 'error', text: 'Please provide a reason for the update.' }); return; }

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();
    const newHeightInCm = getHeightInCm(newHeight, newHeightUnit);
    const newWeightValue = parseFloat(newWeight);
    const newValue = `${newHeightInCm}cm / ${newWeightValue}kg`;

    addEditRequest({
      id: `ER${Date.now()}`,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      vitalId: existingVital.id,
      vitalType: 'height_weight',
      currentValue: existingVital.value,
      newValue,
      requestedBy: user.username,
      requestedAt: timestamp,
      reason: updateReason,
      status: 'pending',
    });

    addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Edit Request',
      details: `Requested Height & Weight update for ${selectedPatient.name} (${selectedPatient.id}): ${existingVital.value} → ${newValue}`,
      ipAddress, deviceName: device, browser, timestamp,
    });

    setShowUpdateModal(false);
    setNewHeight(''); setNewWeight(''); setNewHeightUnit('cm'); setUpdateReason('');
    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Height &amp; Weight Entry</h2>
          <p className="text-sm text-gray-500 mt-1">Search for a patient and record height and weight measurements</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {updateSuccess && (
            <div className="mb-5 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-send-plane-line text-teal-500 text-base"></i>
              Update request submitted. Awaiting admin approval.
            </div>
          )}
          {message && (
            <div className={`mb-5 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              <i className={`${message.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'} text-base`}></i>
              {message.text}
            </div>
          )}

          {/* Patient Search */}
          <div className="mb-6 relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Patient <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by ID, Name, or Mobile Number"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" />
            </div>
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {searchResults.map((patient) => (
                  <button key={patient.id} onClick={() => selectPatient(patient)}
                    className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer">
                    <p className="text-sm font-medium text-gray-800">{patient.name}</p>
                    <p className="text-xs text-gray-500">{patient.id} • {patient.contactNumber}</p>
                  </button>
                ))}
              </div>
            )}
            {showDropdown && searchResults.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                <p className="text-sm text-gray-500">No patients found</p>
              </div>
            )}
          </div>

          {/* Selected Patient Info */}
          {selectedPatient && (
            <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                <i className="ri-user-line text-teal-600"></i>
                Selected Patient
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-800">{selectedPatient.name}</span></div>
                <div><span className="text-gray-500">ID:</span> <span className="font-medium text-gray-800">{selectedPatient.id}</span></div>
                <div><span className="text-gray-500">Age:</span> <span className="font-medium text-gray-800">{selectedPatient.age} yrs</span></div>
                <div><span className="text-gray-500">Gender:</span> <span className="font-medium text-gray-800">{selectedPatient.gender}</span></div>
              </div>
            </div>
          )}

          {/* Already Recorded Banner */}
          {alreadyRecorded && existingVital && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-alert-line text-orange-500"></i>
                    <span className="text-sm font-semibold text-orange-800">Height &amp; Weight Already Recorded</span>
                  </div>
                  <p className="text-sm text-orange-700">Current value: <span className="font-bold">{existingVital.value}</span></p>
                  <p className="text-xs text-orange-600 mt-0.5">Recorded by {existingVital.recordedBy} on {new Date(existingVital.recordedAt).toLocaleString()}</p>
                  {isAdmin() && (
                    <p className="text-xs text-orange-700 mt-2 font-medium">
                      <i className="ri-shield-check-line"></i> As admin, you can directly edit the values below
                    </p>
                  )}
                </div>
                {!isAdmin() && (
                  <button onClick={() => setShowUpdateModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-edit-line"></i>
                    Request Update
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Height */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-ruler-line text-teal-500 text-base"></i>
                </div>
                Height
              </h3>
              <div className="flex gap-3">
                <input type="number" step="0.01" value={height} onChange={(e) => setHeight(e.target.value)}
                  placeholder={heightUnit === 'cm' ? 'e.g. 170' : 'e.g. 5.6'}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!selectedPatient || (alreadyRecorded && !isAdmin())} />
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button type="button" onClick={() => setHeightUnit('cm')} disabled={alreadyRecorded && !isAdmin()}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${heightUnit === 'cm' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
                    cm
                  </button>
                  <button type="button" onClick={() => setHeightUnit('feet')} disabled={alreadyRecorded && !isAdmin()}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${heightUnit === 'feet' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
                    feet
                  </button>
                </div>
              </div>
              {height && heightUnit === 'feet' && !isNaN(parseFloat(height)) && (
                <p className="mt-2 text-xs text-gray-500">≈ {convertFeetToCm(parseFloat(height))} cm</p>
              )}
            </div>

            {/* Weight */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-scales-line text-cyan-500 text-base"></i>
                </div>
                Weight
              </h3>
              <div className="flex items-center gap-3">
                <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)}
                  placeholder="e.g. 65"
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!selectedPatient || (alreadyRecorded && !isAdmin())} />
                <span className="text-sm font-medium text-gray-600 w-8">kg</span>
              </div>
            </div>

            {/* Preview with BMI */}
            {height && weight && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-3">Reading Preview</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Height</p>
                    <p className="text-xl font-bold text-teal-600">
                      {heightUnit === 'feet' ? `${height} ft` : `${height} cm`}
                    </p>
                    {heightUnit === 'feet' && !isNaN(parseFloat(height)) && (
                      <p className="text-xs text-gray-400">≈ {convertFeetToCm(parseFloat(height))} cm</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Weight</p>
                    <p className="text-xl font-bold text-cyan-600">{weight} kg</p>
                  </div>
                </div>
                
                {/* BMI Display */}
                {bmiData && (
                  <div className="pt-4 border-t border-gray-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Body Mass Index (BMI)</p>
                        <p className={`text-2xl font-bold ${bmiData.color}`}>
                          {bmiData.bmi}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">Category</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${bmiData.color} bg-white border-2`}>
                          {bmiData.category}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading || !selectedPatient || !height || !weight || (alreadyRecorded && !isAdmin())}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm">
                {loading ? 'Recording...' : (alreadyRecorded && isAdmin() ? 'Update Height & Weight' : 'Record Height & Weight')}
              </button>
              <button type="button" onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Update Request Modal */}
      {showUpdateModal && existingVital && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Request Height &amp; Weight Update</h3>
              <button onClick={() => setShowUpdateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Current value:</span>
              <span className="ml-2 font-bold text-gray-800">{existingVital.value}</span>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">New Height <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={newHeight} onChange={(e) => setNewHeight(e.target.value)}
                  placeholder={newHeightUnit === 'cm' ? 'e.g. 172' : 'e.g. 5.7'}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm" />
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button type="button" onClick={() => setNewHeightUnit('cm')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${newHeightUnit === 'cm' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600'}`}>
                    cm
                  </button>
                  <button type="button" onClick={() => setNewHeightUnit('feet')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${newHeightUnit === 'feet' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600'}`}>
                    ft
                  </button>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">New Weight (kg) <span className="text-red-500">*</span></label>
              <input type="number" step="0.1" value={newWeight} onChange={(e) => setNewWeight(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                placeholder="e.g. 70" />
            </div>
            
            {/* BMI Preview in Modal */}
            {newBmiData && (
              <div className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-2">New BMI Preview</p>
                <div className="flex items-center justify-between">
                  <p className={`text-xl font-bold ${newBmiData.color}`}>
                    {newBmiData.bmi}
                  </p>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${newBmiData.color} bg-white border`}>
                    {newBmiData.category}
                  </span>
                </div>
              </div>
            )}
            
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Reason for Update <span className="text-red-500">*</span></label>
              <textarea value={updateReason} onChange={(e) => setUpdateReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm resize-none"
                rows={3} placeholder="Explain why this value needs to be updated..." maxLength={300} />
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpdateRequest}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                Submit Request
              </button>
              <button onClick={() => setShowUpdateModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}